import { Component, type ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { isChunkLoadError, requestChunkReloadOnce } from '../lib/lazy-retry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isRecovering: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isRecovering: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isRecovering: false };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack);
    if (isChunkLoadError(error) && requestChunkReloadOnce('ef_chunk_boundary_retry_at')) {
      this.setState({ hasError: true, error: null, isRecovering: true });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isRecovering) {
        return (
          <div className="flex items-center justify-center min-h-[400px] p-8">
            <div className="text-center max-w-md">
              <RefreshCw className="w-10 h-10 text-accent-primary mx-auto mb-4 animate-spin" />
              <h2 className="text-lg font-semibold text-text-primary mb-2">Odświeżam widok</h2>
              <p className="text-sm text-text-secondary">
                Pobieram aktualną wersję panelu.
              </p>
            </div>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center min-h-[400px] p-8">
          <div className="text-center max-w-md">
            <AlertCircle className="w-12 h-12 text-danger mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-text-primary mb-2">Wystąpił błąd</h2>
            <p className="text-sm text-text-secondary mb-4">
              {this.state.error?.message || 'Nieoczekiwany błąd aplikacji.'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null, isRecovering: false });
                window.location.reload();
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent-primary/20 text-accent-primary rounded-lg hover:bg-accent-primary/30 text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Odśwież stronę
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
